import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Briefcase, Building2, CalendarCheck2, ChevronDown, Crown, Film, History, IdCard, LayoutDashboard, LogOut, Menu, Radio, Trophy, UserCheck, UserCircle, Users, X } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { api } from "@/lib/api";
import { CompleteProfileModal } from "@/components/CompleteProfileModal";

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: "Super-admin",
  PROVIDER: "Proveedor",
  CLUB: "Usuario de club",
  REFEREE: "Árbitro",
};

export function Layout() {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isOnMatchesRoute = location.pathname === "/partidos" || location.pathname === "/partidos-en-directo";
  const [matchesOpen, setMatchesOpen] = useState(isOnMatchesRoute);

  async function handleLogout() {
    await api.post("/auth/logout").catch(() => {});
    clear();
    navigate("/login", { replace: true });
  }

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  // En <body> (no en el div de abajo) a propósito: los Modal/ConfirmDialog usan un Portal de
  // Radix que monta su contenido como hijo directo de <body>, fuera del árbol de este
  // componente — si el atributo solo estuviera en el div raíz, los botones dentro de cualquier
  // modal se quedarían siempre en verde (no heredan la variable CSS).
  useEffect(() => {
    if (user?.role) document.body.dataset.roleTheme = user.role;
    return () => {
      delete document.body.dataset.roleTheme;
    };
  }, [user?.role]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive
        ? isSuperAdmin
          ? "bg-violet-500/15 text-violet-300"
          : "bg-brand-500/15 text-brand-300"
        : "text-ink-100/70 hover:bg-white/5 hover:text-white"
    }`;

  const initial = user?.nombre?.charAt(0).toUpperCase() ?? "?";
  // Listas explícitas de roles (NO "todo lo que no sea CLUB"): un árbitro no gestiona nada del
  // club, y con la comprobación antigua habría visto en el menú secciones a las que el backend
  // le iba a denegar el acceso.
  const isAdminRole = user?.role === "SUPER_ADMIN" || user?.role === "PROVIDER";
  const canManageBookings =
    isAdminRole || (user?.role === "CLUB" && user.permissions.includes("MANAGE_BOOKINGS"));
  const canManageSubusers =
    isAdminRole || (user?.role === "CLUB" && user.permissions.includes("MANAGE_SUBUSERS"));
  const canSeeChampionships = isAdminRole || user?.role === "CLUB";

  const scopeLabel =
    user?.role === "PROVIDER"
      ? (user.ownedClubs?.length === 1 ? user.ownedClubs[0].nombre : `${user?.ownedClubs?.length ?? 0} clubes`)
      : user?.role === "CLUB"
        ? (user?.club?.nombre ?? "")
        : user?.role === "REFEREE"
          ? ""
          : "Todos los clubes de la plataforma";

  const sidebarContent = (
    <>
      <div className="mb-8 flex flex-col gap-3 px-2">
        <div className="flex items-center justify-between lg:justify-start lg:gap-2">
          <div className="flex items-center gap-2">
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-xl text-lg ${
                isSuperAdmin ? "bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/30" : "bg-brand-500/20"
              }`}
            >
              {isSuperAdmin ? <Crown className="h-4.5 w-4.5 text-white" /> : "🎾"}
            </span>
            <span className="text-lg font-bold text-white">PuntoPadel</span>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="rounded-lg p-1.5 text-ink-100/60 hover:bg-white/10 hover:text-white lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <span
          className={`inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
            isSuperAdmin
              ? "bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 text-violet-300 ring-1 ring-violet-500/30"
              : "bg-white/5 text-ink-100/70 ring-1 ring-white/10"
          }`}
        >
          {isSuperAdmin && <Crown className="h-3.5 w-3.5" />}
          {roleLabels[user?.role ?? ""]}
          {scopeLabel && <span className={isSuperAdmin ? "text-violet-400/70" : "text-ink-100/40"}>·</span>}
          {scopeLabel}
        </span>
      </div>

      <nav className="flex flex-col gap-1" onClick={() => setMenuOpen(false)}>
        <NavLink to="/" end className={linkClass}>
          <LayoutDashboard className="h-4.5 w-4.5" />
          Panel
        </NavLink>
        {user?.role === "SUPER_ADMIN" && (
          <NavLink to="/proveedores" className={linkClass}>
            <Briefcase className="h-4.5 w-4.5" />
            Proveedores
          </NavLink>
        )}
        {user?.role === "SUPER_ADMIN" && (
          <NavLink to="/arbitros" className={linkClass}>
            <UserCheck className="h-4.5 w-4.5" />
            Árbitros
          </NavLink>
        )}
        {(user?.role === "SUPER_ADMIN" || user?.role === "PROVIDER") && (
          <NavLink to="/clubes" className={linkClass}>
            <Building2 className="h-4.5 w-4.5" />
            Clubes
          </NavLink>
        )}
        {canSeeChampionships && (
          <NavLink to="/campeonatos" className={linkClass}>
            <Trophy className="h-4.5 w-4.5" />
            Campeonatos
          </NavLink>
        )}
        {canManageBookings && (
          <NavLink to="/reservas" className={linkClass}>
            <CalendarCheck2 className="h-4.5 w-4.5" />
            Partidas
          </NavLink>
        )}
        {canManageBookings && (
          <div>
            <button
              type="button"
              onClick={() => setMatchesOpen((o) => !o)}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                isOnMatchesRoute
                  ? isSuperAdmin
                    ? "bg-violet-500/15 text-violet-300"
                    : "bg-brand-500/15 text-brand-300"
                  : "text-ink-100/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Film className="h-4.5 w-4.5" />
              Partidos
              <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${matchesOpen ? "rotate-180" : ""}`} />
            </button>
            {matchesOpen && (
              <div className="mt-1 ml-4 flex flex-col gap-1 border-l border-white/10 pl-3">
                <NavLink to="/partidos" className={linkClass}>
                  <Film className="h-4 w-4" />
                  Partidos jugados
                </NavLink>
                <NavLink to="/partidos-en-directo" className={linkClass}>
                  <Radio className="h-4 w-4" />
                  Partidos en directo
                </NavLink>
              </div>
            )}
          </div>
        )}
        {canManageBookings && (
          <NavLink to="/jugadores" className={linkClass}>
            <IdCard className="h-4.5 w-4.5" />
            Jugadores
          </NavLink>
        )}
        {canManageSubusers && (
          <NavLink to="/usuarios" className={linkClass}>
            <Users className="h-4.5 w-4.5" />
            Usuarios
          </NavLink>
        )}
        {canManageSubusers && (
          <NavLink to="/auditoria" className={linkClass}>
            <History className="h-4.5 w-4.5" />
            Logs
          </NavLink>
        )}
        <NavLink to="/perfil" className={linkClass}>
          <UserCircle className="h-4.5 w-4.5" />
          Mi perfil
        </NavLink>
      </nav>

      <div
        className={`mt-auto flex items-center gap-3 rounded-xl p-3 ${
          isSuperAdmin ? "bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 ring-1 ring-violet-500/20" : "bg-white/5"
        }`}
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${
            isSuperAdmin ? "bg-gradient-to-br from-violet-500 to-fuchsia-500 ring-2 ring-violet-400/30" : "bg-brand-500"
          }`}
        >
          {isSuperAdmin ? <Crown className="h-4 w-4" /> : initial}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{user?.nombre}</p>
          <p className={`truncate text-xs font-medium ${isSuperAdmin ? "text-violet-300" : "text-ink-100/50"}`}>
            {user ? roleLabels[user.role] : ""}
          </p>
        </div>
        <button
          onClick={handleLogout}
          title="Cerrar sesión"
          className="shrink-0 rounded-lg p-1.5 text-ink-100/50 transition-colors hover:bg-white/10 hover:text-white"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-ink-50">
      <CompleteProfileModal />
      {/* Sidebar: cajón deslizante en movil, fijo en escritorio */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-72 max-w-[85vw] flex-col bg-ink-950 px-4 py-6 transition-transform duration-200 ease-out lg:static lg:z-auto lg:w-64 lg:max-w-none lg:translate-x-0 ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarContent}
      </aside>

      {menuOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-950/50 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-ink-100 bg-white px-4 py-4 sm:px-6 lg:hidden">
          <button
            onClick={() => setMenuOpen(true)}
            className="rounded-lg p-1.5 text-ink-500 hover:bg-ink-50"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="text-lg font-bold text-ink-900">PuntoPadel</span>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
